import { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, InputAdornment } from '@mui/material';

import {
  FBoxContainer,
  FHAntdDateTimePicker,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHSelect,
} from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { getTargetStagesForGoalType, validateV2Goal } from './utils/general';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import { capitalizeFirstLetter, disabledFutureDate } from '../../../../../helpers/';
import GoalTargetItem from './GoalTargetItem';
import StageChangeConfirmation from './targetStages/BulkStageChangeDialog';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS, TargetStages } from '../common/constants/general';
import PlusIcon from '../../../../svgs/PlusIcon';
import ChangeStageIcon from '../../../../svgs/ChangeStageIcon';

import type { ITreatmentPlanGoal, ITreatmentPlanGoalTarget } from '../../../../../utils/types';

function isTargetStage(value: string | number): value is TargetStages {
  return Object.values(TargetStages).some((stage) => stage === value);
}

interface GoalTargetsProps {
  goal: ITreatmentPlanGoal;
  targetSaving: boolean;
  onAddTarget: () => void;
  onUpdateField: (index: number, key: string, value: any) => void;
  onSaveTarget: (index: number, target: ITreatmentPlanGoalTarget) => void;
  onDeleteTarget: (index: number) => void;
  onUpdateTargetActivation: (index: number, reason: string) => void;
  onCancelTargetChange: (index: number, target: ITreatmentPlanGoalTarget | null) => void;
  onBulkStageChange: (
    selectedTargets: ITreatmentPlanGoalTarget[],
    selectedStage: string,
    selectedReason: string,
    selectedStageDate: string,
  ) => void;
  onAddTargetWithValues: (data: any) => void;
  trialSummary: any;
}

function GoalTargets(props: GoalTargetsProps) {
  const {
    goal,
    targetSaving,
    onUpdateField,
    onAddTarget,
    onSaveTarget,
    onDeleteTarget,
    onAddTargetWithValues,
    onUpdateTargetActivation,
    onCancelTargetChange,
    onBulkStageChange,
    trialSummary: _trialSummary,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [stageDate, setStageDate] = useState<string>('');
  const [stage, setStage] = useState<TargetStages | null>(null);
  const [reason, setReason] = useState<string>('');
  const [invalidGoalFields, setInvalidGoalFields] = useState<string[]>([]);
  const [showGoalValidation, setShowGoalValidation] = useState(false);
  const [scrollToLastTarget, setScrollToLastTarget] = useState(false);
  const [isStageChangeConfirmationOpen, setIsStageChangeConfirmationOpen] = useState(false);

  const lastTargetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (lastTargetRef.current && scrollToLastTarget) {
      lastTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollToLastTarget(false);
    }
  }, [goal?.targets]);

  useEffect(() => {
    setStage(null);
    setReason('');
    setStageDate('');
    setIsStageChangeConfirmationOpen(false);
  }, [goal.id]);

  const selectableStages = getTargetStagesForGoalType(goal.goal_type).filter(
    (stage) => stage.value !== TargetStages.Baseline && stage.value !== TargetStages.Resume,
  );

  const getInvalidGoalField = () => {
    const errors = validateV2Goal(goal);

    // Exclude the 'targets' and 'description' keys from the errors object.
    // The 'targets' key is excluded because a new goal won't have any targets yet.
    // The 'description' key is excluded since it won't have a value when the add target button is clicked directly, bypassing the onBlur event.
    const filteredErrors = Object.entries(errors).filter(
      ([key]) => key !== 'targets' && key !== 'description',
    );

    const invalidFields = filteredErrors.flatMap(([key, value]) => {
      if (key === 'measurement') {
        return value ? ['Mastering criteria'] : [];
      }
      return value ? [key.replace(/_/g, ' ')] : [];
    });
    return invalidFields;
  };

  const handleAddTarget = () => {
    const invalidFields = getInvalidGoalField();
    if (invalidFields.length > 0) {
      setInvalidGoalFields(invalidFields);
      setShowGoalValidation(true);
      return;
    }
    onAddTarget();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }} data-testid="GoalTargetV2Item">
      {goal?.id && isTpEditable && (
        <Box sx={{ mt: 2, mb: 1, backgroundColor: FORTA_BLUE.light3, borderRadius: '16px', p: 3 }}>
          <Typography sx={{ textAlign: 'left', fontSize: 21, fontWeight: 600, mb: 1 }}>
            Change stage for multiple targets
          </Typography>
          <Typography sx={{ textAlign: 'left', fontSize: 15, fontWeight: 400, mb: 2 }}>
            After clicking <strong>&apos;Change stage&apos;</strong>, a popup will appear, allowing
            you to select a list of targets to change the stage for.
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={2.5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography fontWeight="bold" align="left" mb={1}>
                  Date
                </Typography>
                <FHAntdDateTimePicker
                  data-testid={`expectedMasteryDateForBulkStageChange_${goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
                  format="MM/DD/YY"
                  allowClear={true}
                  inputHeight={40}
                  style={{ width: '100%' }}
                  disabled={!isTpEditable}
                  disabledDate={disabledFutureDate}
                  value={stageDate}
                  onChange={(value: string) => setStageDate(value)}
                />
              </Box>
            </Grid>

            <Grid item xs={2.5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography fontWeight="bold" align="left" mb={1}>
                  Stage
                </Typography>
                <FHSelect
                  data-testid={`stageLevelDropDown_${goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
                  sx={{ width: '100%' }}
                  inputHeight={40}
                  disabled={!isTpEditable}
                  renderValue={stage ? undefined : () => <Typography>Select stage</Typography>}
                  options={selectableStages}
                  optionDataTestIdPrefix="baseLineLevelOption"
                  value={stage}
                  onUpdate={(value) => setStage(isTargetStage(value) ? value : null)}
                />
              </Box>
            </Grid>

            {(stage === TargetStages.OnHold || stage === TargetStages.Discontinued) && (
              <Grid item xs={4.5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography fontWeight="bold" align="left" mb={1}>
                    Reason for change
                  </Typography>
                  <FHInputField
                    inputHeight={40}
                    data-testid="reasonForChangeTextField"
                    sx={{ width: '100%' }}
                    type="text"
                    disabled={!isTpEditable}
                    value={reason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                    error={!reason}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {stage === TargetStages.OnHold
                            ? 'On Hold - '
                            : stage === TargetStages.Discontinued
                              ? 'Discontinued - '
                              : undefined}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            )}

            <Grid item sx={{ ml: 'auto', alignSelf: 'flex-end', mb: 0.5 }}>
              <FHButton
                onClick={() => setIsStageChangeConfirmationOpen(true)}
                startIcon={<ChangeStageIcon />}
                disabled={
                  !stageDate ||
                  !stage ||
                  (stage === TargetStages.OnHold && !reason) ||
                  (stage === TargetStages.Discontinued && !reason)
                }
                data-testid={`changeStageButton_${goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
              >
                Change Stage
              </FHButton>
            </Grid>
          </Grid>
        </Box>
      )}
      {
        <FBoxContainer
          title="Targets"
          colorTheme={Themes.Primary}
          titleVariant={'h4'}
          sx={{ mt: 2 }}
          headerStyle={{
            paddingLeft: '18px',
            paddingRight: '18px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            width: '100%',
            marginLeft: 0,
            '& h4': {
              fontSize: '18px',
            },
          }}
          contentStyle={{
            py: 2,
            padding: 0,
          }}
        >
          {goal.targets?.map((target, index) => (
            <Box
              key={target?.id ?? index}
              ref={index === goal.targets.length - 1 ? lastTargetRef : null}
            >
              <GoalTargetItem
                index={index}
                goalId={goal.id ?? 0}
                goal={goal}
                target={target}
                saving={targetSaving}
                goalType={goal.goal_type}
                goalActive={goal.is_active}
                onSave={(target) => onSaveTarget(index, target)}
                onDelete={() => onDeleteTarget(index)}
                onUpdateField={(key, value) => onUpdateField(index, key, value)}
                onAddTargetWithValues={(data) => {
                  const invalidFields = getInvalidGoalField();
                  if (invalidFields.length > 0) {
                    setInvalidGoalFields(invalidFields);
                    setShowGoalValidation(true);
                  } else {
                    onAddTargetWithValues(data);
                    setScrollToLastTarget(true);
                  }
                }}
                onUpdateTargetActivation={(reason: string) =>
                  onUpdateTargetActivation(index, reason)
                }
                onCancelTargetChange={(target) => onCancelTargetChange(index, target)}
              />
            </Box>
          ))}
          {isTpEditable && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mt: 1 }}>
              <FHButton
                data-testid={`addTargetButton_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
                startIcon={<PlusIcon color={FORTA_WHITE} />}
                colorTheme={Themes.Primary}
                sx={{ width: 'fit-content', height: '48px' }}
                onClick={handleAddTarget}
                error={goal?.targets?.length === 0}
              >
                Add target
              </FHButton>
            </Box>
          )}
        </FBoxContainer>
      }
      <FHConfirmDialog
        open={showGoalValidation}
        colorTheme={Themes.Primary}
        title={'Incomplete Goal data'}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          padding: '32px',
          maxHeight: '85vh',
          height: 'auto',
          width: '650px',
          maxWidth: '700px',
        }}
        sx={{
          textAlign: 'left',
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        contentRenderer={() => (
          <Box sx={{ textAlign: 'left', padding: '20px 0px 20px 0px' }}>
            <Typography fontSize={18}>
              Please complete the following required fields in the goal and save them before adding
              a new target:
            </Typography>
            <Box sx={{ mt: 2 }} component="ul">
              {invalidGoalFields.map((field, index) => (
                <Typography component="li" key={index} sx={{ fontSize: 18, marginBottom: 0.5 }}>
                  {capitalizeFirstLetter(field)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        submittext={'Okay'}
        hideCancelButton
        onClose={() => setShowGoalValidation(false)}
        onSubmit={() => setShowGoalValidation(false)}
      />
      {stage && (
        <StageChangeConfirmation
          open={isStageChangeConfirmationOpen}
          selectedDate={stageDate}
          targets={goal?.targets ?? []}
          selectedStage={stage}
          goalType={goal?.goal_type}
          onClose={(_, reason) => {
            if (reason && reason === 'backdropClick') return;
            setStage(null);
            setReason('');
            setStageDate('');
            setIsStageChangeConfirmationOpen(false);
          }}
          onApply={(selectedTargets: ITreatmentPlanGoalTarget[]) => {
            onBulkStageChange(selectedTargets, stage, reason, stageDate);
            setStage(null);
            setReason('');
            setStageDate('');
            setIsStageChangeConfirmationOpen(false);
          }}
        />
      )}
    </Box>
  );
}

export default GoalTargets;
